/**
 * Área ARQUIVOS ADMINISTRATIVOS: a pasta fixa `admin-arquivos` do bucket `materiais`, fora de
 * qualquer cliente.
 *
 * Cobre as quatro chamadas de Storage da área no legado: listar a pasta (`admin.html:6381`), o link
 * assinado de cada arquivo (`6345`), excluir (`6366`) e enviar (`6432`).
 *
 * POR QUE LISTAR, ABRIR E EXCLUIR RECEBEM A PASTA: no legado as três são as MESMAS funções da aba
 * Arquivos da ficha da cliente (`listarArquivosNaPasta` e `criarLinhaArquivoAdmin`, `6344-6395`),
 * chamadas com outra pasta. Aqui elas ficam iguais, e a ficha pode usá-las sem uma segunda cópia.
 * O envio é o único passo próprio da área: pasta fixa e `upsert`.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  BUCKET_MATERIAIS,
  CAMINHO_ARQUIVOS_ADMIN,
  clienteDe,
  executar,
  type FonteDeDados,
  type Resultado,
} from '../nucleo.js';

/** Um item da listagem do Storage. O legado só usa o nome (`arquivo.name`, `admin.html:6348`). */
export interface ArquivoDaPasta {
  // O nome do arquivo dentro da pasta, que é também o fim do caminho.
  readonly name: string;
}

/**
 * Lista uma pasta do bucket `materiais`.
 * Legado: `admin.html:6381`, `sb.storage.from('materiais').list(caminho)`, sem opções: o Storage
 * aplica o padrão dele (até 100 itens, em ordem de nome), e o legado mostra o que vier.
 */
export function listarArquivosDaPasta(fonte: FonteDeDados, caminho: string): Promise<Resultado<ArquivoDaPasta[]>> {
  // Sem segundo argumento, como o legado: mudar limite ou ordem mudaria o que a pessoa vê.
  return executar<ArquivoDaPasta[]>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).list(caminho));
}

/** A pasta fixa dos arquivos administrativos, com a mesma chamada da lista genérica. */
export function listarArquivosAdministrativos(fonte: FonteDeDados): Promise<Resultado<ArquivoDaPasta[]>> {
  // `carregarArquivosAdmin` do legado (`admin.html:6420`) é esta lista com a pasta fixa.
  return listarArquivosDaPasta(fonte, CAMINHO_ARQUIVOS_ADMIN);
}

// Uma hora, em segundos: o prazo do link "Abrir" no legado (`admin.html:6345`).
const VALIDADE_DO_LINK_EM_SEGUNDOS = 3600;

/**
 * O link assinado de um arquivo, válido por uma hora.
 * Legado: `admin.html:6345`, `createSignedUrl(`${caminho}/${arquivo.name}`, 3600)`.
 *
 * O legado ignora a falha e deixa o link apontando para `#`; a tela decide isso pelo `ok`.
 */
export function linkAssinadoDoArquivo(
  fonte: FonteDeDados,
  caminho: string,
  nome: string,
): Promise<Resultado<{ readonly signedUrl: string }>> {
  return executar<{ readonly signedUrl: string }>(
    clienteDe(fonte).storage.from(BUCKET_MATERIAIS).createSignedUrl(`${caminho}/${nome}`, VALIDADE_DO_LINK_EM_SEGUNDOS),
  );
}

/**
 * Exclui um arquivo de uma pasta.
 * Legado: `admin.html:6366`, `sb.storage.from('materiais').remove([`${caminho}/${arquivo.name}`])`.
 */
export function excluirArquivoDaPasta(fonte: FonteDeDados, caminho: string, nome: string): Promise<Resultado<unknown>> {
  // A lista com um caminho só, como o legado manda.
  return executar<unknown>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).remove([`${caminho}/${nome}`]));
}

// O tipo que o próprio SDK aceita como conteúdo de upload, lido da assinatura, e não reescrito.
type ConteudoDoUpload = Parameters<ReturnType<ReturnType<typeof clienteDe>['storage']['from']>['upload']>[1];

/**
 * Envia um arquivo para a pasta administrativa, sobrescrevendo o de mesmo nome.
 * Legado: `admin.html:6431-6432`, `upload(`admin-arquivos/${sanitizarNomeArquivo(nome)}`, arquivo,
 * { upsert: true })`.
 *
 * O NOME CHEGA JÁ SANITIZADO pela tela (`sanitizarNomeArquivo` de `@studiolu/dados`), como no
 * legado, onde o caminho é montado no clique; aqui só se junta a pasta fixa.
 */
export function enviarArquivoAdministrativo(
  fonte: FonteDeDados,
  nomeSanitizado: string,
  conteudo: ConteudoDoUpload,
): Promise<Resultado<unknown>> {
  return executar<unknown>(
    clienteDe(fonte)
      .storage.from(BUCKET_MATERIAIS)
      // `upsert: true`: enviar de novo um arquivo com o mesmo nome o substitui, como no legado.
      .upload(`${CAMINHO_ARQUIVOS_ADMIN}/${nomeSanitizado}`, conteudo, { upsert: true }),
  );
}
